import { observer } from 'mobx-react-lite';
import ProposalSection, { ProposalSectionEditableProps } from './ProposalSection';
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto';
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import IconButton from "@mui/material/IconButton"
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from 'theme/useTheme';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { useProposalsViewStore } from 'features/proposals/ProposalsViewStore';
import DateUtils from '@gap/utils/date-utils';

type ProposalSectionHeaderProps = {
   proposal: ProposalDto,
   sectionProps?: ProposalSectionEditableProps,
   advisorLogoUrl?: string,
}

export default observer(function ProposalSectionHeader({ sectionProps, advisorLogoUrl, proposal }: ProposalSectionHeaderProps) {
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
   const isTouchDevice = useMediaQuery('(hover: none)')
   const { advisorOrg, clientOrg } = useProposalsViewStore()
   const proposalDate = DateUtils.formatDate(proposal.sentAt ? new Date(proposal.sentAt) : new Date(), 'date-full-month')

   const showSectionEditUi = {
      borderColor: theme.palette.gap.primary['200'],
      '& .edit': {
         visibility: 'visible',
      }
   }

   return (
      <ProposalSection
         shouldSpanPage
         sx={{
            paddingY: { xs: 6, sm: 16 },
            gap: 6,
            borderBottom: `1px solid ${theme.palette.gap.primary['200']}`,
            '> *': {
               textAlign: 'center',
               alignItems: 'center',
               justifyContent: 'center',
            },
         }}
      >
         <Stack sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            padding: 2,
            ...((sectionProps?.editable && (isTouchDevice || showSectionEditUi)) ? {
               paddingLeft: 8,
            } : {}),
            border: '1px solid transparent',
            borderRadius: '16px',
            ...(sectionProps?.editable ? isTouchDevice
                  ? showSectionEditUi
                  : { '&:hover': showSectionEditUi } : {}),
         }}>
            {advisorLogoUrl && <img src={advisorLogoUrl} alt={advisorOrg.name} style={{
               width: '200px',
               maxWidth: '100%',
               objectFit: 'contain',
            }} />}
            {sectionProps?.editable && <IconButton
               className="edit"
               size="small"
               onClick={sectionProps.onEdit}
               sx={{
                  visibility: 'hidden',
                  ...(isMobile ? { margin: 2 } : {
                        width: '32px', height: '32px', '> *': { fontSize: '20px' },
                     })
               }}
            ><EditOutlined /></IconButton>}
         </Stack>
         <Typography variant="h1">Proposal</Typography>
         <Typography variant="subtitle1" sx={{ color: 'secondary.light' }}>
            Prepared for {clientOrg?.name} by {advisorOrg.name} on {proposalDate}
         </Typography>
      </ProposalSection>
   );
})
